import { ArrowLeft, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { UserForm } from '@/components/features/users/UserForm'
import { useCreateUser } from '@/hooks/operations/use-users'
import { type CreateUserFormData } from '@/lib/validation/user-schemas'
import { type CreateUserRequest } from '@/models/domain/keycloak-user'

function CreateUserHeader({ onBack }: { onBack: () => void }) {
  return (
    <div className="mb-6">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to users
      </button>

      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
          <UserPlus className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create User</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Add a new user to the portal</p>
        </div>
      </div>
    </div>
  )
}

function ensureArray(value: unknown): string[] {
  if (Array.isArray(value)) return value as string[]
  return []
}

function boolToAttr(value: boolean | undefined): string[] {
  return value ? ['true'] : ['false']
}

function buildCreateAttributes(formData: CreateUserFormData) {
  return {
    system: ensureArray(formData.attributes.systems),
    systems_all: boolToAttr(formData.attributes.systems_all),
    dba: ensureArray(formData.attributes.dbas),
    district: ensureArray(formData.attributes.districts),
    location: ensureArray(formData.attributes.locations),
    locations_all: boolToAttr(formData.attributes.locations_all),
    section: ensureArray(formData.attributes.sections),
    sections_all: boolToAttr(formData.attributes.sections_all),
    locked: boolToAttr(formData.accountStatus.locked),
  }
}

function buildCredentials(formData: CreateUserFormData) {
  if (!formData.basicInfo.password) return undefined
  return [
    {
      type: 'password' as const,
      value: formData.basicInfo.password,
      temporary: formData.basicInfo.temporaryPassword || false,
    },
  ]
}

function buildCreateRequest(formData: CreateUserFormData): CreateUserRequest {
  return {
    username: formData.basicInfo.username,
    email: formData.basicInfo.email,
    firstName: formData.basicInfo.firstName,
    lastName: formData.basicInfo.lastName,
    enabled: formData.accountStatus.enabled,
    emailVerified: formData.accountStatus.emailVerified || false,
    credentials: buildCredentials(formData),
    realmRoles: Object.entries(formData.realmRoles)
      .filter(([_key, value]) => value)
      .map(([key]) => key),
    clientRoles: formData.clientRoles,
    attributes: buildCreateAttributes(formData),
  }
}

export function CreateUserPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  const createUserMutation = useCreateUser()

  const handleSubmit = async (formData: CreateUserFormData) => {
    setError(null)

    try {
      await createUserMutation.mutateAsync(buildCreateRequest(formData))
      navigate('/operations/users', {
        state: { success: `User ${formData.basicInfo.username} created successfully!` },
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
      console.error('Failed to create user:', err)
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <CreateUserHeader
        onBack={() => {
          navigate('/operations/users')
        }}
      />
      <UserForm
        mode="create"
        onSubmit={handleSubmit}
        isLoading={createUserMutation.isPending}
        error={error}
      />
    </div>
  )
}
